
import { defineComponent, onBeforeMount, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'

// @new-ui
import NewButton from '@/ui-kit/NewButton/index.vue'

// @modules/components
import ForecastingDelivery from '@/modules/NewDispatcher/components/ForecastingDelivery/index.vue'
import ForecastingPlanning from '@/modules/NewDispatcher/components/ForecastingPlanning/index.vue'
import ForecastingWave from '@/modules/NewDispatcher/components/ForecastingWave/index.vue'
import ModalPanel from '@/modules/NewDispatcher/components/ModalPanel/index.vue'
import WaveInvoicePanelInformation from '@/modules/NewDispatcher/components/WaveInvoicePanelInformation/index.vue'

// @types
import { Wave, WaveInvoice } from '@/new-services/types/waves'

import { UpdateCargoPayload } from '@/new-services/types/invoice'

// @services
import wavesAPI from '@/new-services/waves.service'
import invoiceAPI from '@/new-services/invoice.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'

export default defineComponent({
    components: {
        PageTitle,
        NewButton,
        Loader,
        NewModal,
        ForecastingWave,
        ModalPanel,
        WaveInvoicePanelInformation,
        ForecastingDelivery,
        ForecastingPlanning,
    },
    setup() {
        const new_dispatcher_store = useNewDispatcherStore()

        const router = useRouter()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            waves: [] as Wave[],
        })

        const invoice_panel = reactive({
            is_opened: false,
            is_loading: false,
            invoice: null as WaveInvoice | null,
        })

        const delete_state = reactive({
            is_opened: false,
            is_loading: false,
            wave: null as Wave | null,
        })

        const loadWaves = async () => {
            // new_dispatcher_store.validation$.$touch()
            // if (new_dispatcher_store.validation$.dispatcherSectorId.$error) return

            try {
                state.is_loading = true

                const response = await wavesAPI.getWaves({
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                })
                state.waves = response.data.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка волн произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadWaves)

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectorIds,
            () => {
                loadWaves()
            }
        )

        const openInvoicePanel = (invoice: WaveInvoice) => {
            invoice_panel.invoice = { ...invoice }
            invoice_panel.is_opened = true
        }

        const closeInvoicePanel = () => {
            invoice_panel.is_opened = false
            invoice_panel.invoice = null
        }

        const startDelete = (wave: Wave) => {
            delete_state.wave = { ...wave }
            delete_state.is_opened = true
        }

        const cancelDelete = () => {
            delete_state.is_opened = false
            delete_state.wave = null
        }

        const deleteWave = async () => {
            try {
                delete_state.is_loading = true

                const wave = delete_state.wave as Wave
                await wavesAPI.deleteWave(wave.id)

                notifications.notify({ type: 'success', title: 'Волна удалена' })
                cancelDelete()

                loadWaves()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При удалении волны произошла ошибка' })
            } finally {
                delete_state.is_loading = false
            }
        }

        const onWaveUpdate = (wave_id: number) => {
            router.push(`/new-dispatcher/waves/${wave_id}/update`)
        }

        const updateInvoice = async ({
            invoice_id,
            payload,
        }: {
            invoice_id: number
            payload: UpdateCargoPayload
        }) => {
            try {
                invoice_panel.is_loading = true

                await invoiceAPI.updateCargoPayload(invoice_id, payload)

                notifications.notify({ type: 'success', title: 'Данные накладной сохранены' })
                closeInvoicePanel()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении данных накладной произошла ошибка',
                })
            } finally {
                invoice_panel.is_loading = false
            }
        }

        return {
            new_dispatcher_store,
            state,
            invoice_panel,
            delete_state,
            loadWaves,
            openInvoicePanel,
            closeInvoicePanel,
            onWaveUpdate,
            updateInvoice,
            //
            startDelete,
            deleteWave,
            cancelDelete,
        }
    },
})
